import {
	Icon,
	ProgressBarBox,
	Typography,
	UploadContainer,
} from '@/components/common';
import Icons from '@/const/icons';
import Images from '@/const/images';
import { logApiError } from '@/lib/logApiError';
import { hasPdfSignature, isPdfEncrypted } from '@/lib';
import clsx from 'clsx';
import Image from 'next/image';
import * as React from 'react';

interface UploadDocumentProps {
	id: string;
	file: File | null;
	setFile: (file: File | null) => void;
	typeDocument?: string;
	validateFile?: (file: File) => boolean;
	classNameDescription?: string;
	accept?: string;
	maxFileSizeInMB?: number;
	errorMaxSizeMessage?: string;
	variant?: 'default' | 'pill';
}

export const UploadDocument: React.FC<UploadDocumentProps> = ({
	id,
	file,
	setFile,
	typeDocument = 'file',
	validateFile,
	classNameDescription,
	accept,
	maxFileSizeInMB = 25,
	errorMaxSizeMessage,
	variant = 'default',
}) => {
	const isPill = variant === 'pill';
	const url = window.location.href;
	const [progress, setProgress] = React.useState(file ? 100 : 0);
	const [error, setError] = React.useState<string>();

	const handleDropFile = (e: React.DragEvent<HTMLDivElement>): void => {
		e.preventDefault();
		e.stopPropagation();
		const droppedFile = e.dataTransfer.files[0];
		setProgress(0);
		handleUploadFile(droppedFile);
	};

	const handleRemoveFile = (): void => {
		setFile(null);
		setProgress(0);
	};

	const simulateProgress = async (max: number): Promise<void> => {
		return new Promise((resolve) => {
			let currentProgress = 0;
			const interval = setInterval(() => {
				currentProgress += 10;
				setProgress(currentProgress);
				if (currentProgress >= max) {
					clearInterval(interval);
					resolve();
				}
			}, 300);
		});
	};

	const handleUploadFile = async (file: File): Promise<void> => {
		setProgress(0); // Reset progress
		setError(undefined); // reset error message

		await simulateProgress(70); // Only go up to 80% until upload completes

		if (accept === '.pdf') {
			const arrayBuffer = await file.arrayBuffer();
			if (file.type !== 'application/pdf' || !hasPdfSignature(arrayBuffer)) {
				setError(
					'Error Upload failed: Ensure the document is in PDF format, and under 25 MB.'
				);
				handleRemoveFile();
				return;
			}
			if (isPdfEncrypted(arrayBuffer)) {
				setError(
					'This document is password-protected and cannot be sent. Please upload an unprotected version of the file.'
				);
				handleRemoveFile();
				return;
			}
		}

		if (maxFileSizeInMB && file.size > maxFileSizeInMB * 1024 * 1024) {
			setError(
				errorMaxSizeMessage ||
					'The file you tried to upload is too heavy. Please try again with a file up to 25 MB.'
			);
			logApiError({
				status: 400,
				message:
					'The file you tried to upload is too heavy. Please try again with a file up to 25 MB.',
				config: { data: null, url: url },
			});
			handleRemoveFile();
			return;
		}

		if (validateFile && !validateFile(file)) return; // validate file any function

		setProgress(100);
		setFile(file);
	};

	const getFileName = (): string => {
		if (progress == 100) {
			if (file && typeof file !== 'string' && 'name' in file) return file.name;
		}

		return '';
	};

	return (
		<div
			id="content"
			className={clsx(
				isPill
					? 'border-2 border-dashed rounded-xl bg-[#F9FAFB]'
					: 'md:px-10 border-1 border-opacity-80 rounded-[32px] border border-dashed border-opacity-primary bg-gray-20',
				{
					'border-opacity-primary': !error && !isPill,
					'border-[#E5E7EB]': !error && isPill,
					'border-red-600': error,
				}
			)}
			onDrop={handleDropFile}
			onDragOver={(e) => e.preventDefault()}
		>
			<div
				className={clsx(
					'flex flex-col items-center',
					isPill ? 'py-8 px-8 gap-4' : 'py-6 px-8 gap-8'
				)}
			>
				{isPill && (
					<div className="w-14 h-14 rounded-full bg-[#8A5AFB]/10 flex items-center justify-center">
						<Image
							src={Images.uploadCloud}
							alt="upload"
							width={24}
							height={24}
						/>
					</div>
				)}
				<div className={clsx('max-w-[350px]', classNameDescription)}>
					{isPill ? (
						<>
							<Typography
								type="regular-14"
								className="text-gray-35 text-center"
							>
								Drag and drop your {typeDocument} here, or click to browse from
								your device.
							</Typography>
							{progress === 0 && (
								<Typography
									type="regular-12"
									className="text-gray-30 text-center mt-1"
								>
									Supported formats: PDF.
								</Typography>
							)}
						</>
					) : (
						<Typography type="regular-14" className="opacity-60 text-center">
							Drag and drop your {typeDocument} here, or click to browse from
							your device
						</Typography>
					)}
				</div>
				{progress > 0 && (
					<div className="w-48">
						<ProgressBarBox
							percent={progress}
							handleRemove={handleRemoveFile}
							fileName={getFileName()}
							isShowPrevFile={false}
						/>
					</div>
				)}
				{progress === 0 && !file && (
					<UploadContainer id={id} setFile={handleUploadFile} accept={accept}>
						{isPill ? (
							<div className="flex cursor-pointer items-center bg-primary rounded-full text-base font-semibold text-white gap-2 px-6 h-[30px]">
								Browse File
								<Icon src={Icons.folderOpen} className="!w-4 !h-4" />
							</div>
						) : (
							<div className="flex cursor-pointer items-center bg-primary rounded-full text-base font-semibold text-white gap-2 px-6 h-[30px] py-4">
								Upload
								<Icon src={Icons.documentUpload} className="!w-4 !h-4" />
							</div>
						)}
					</UploadContainer>
				)}
				{error && (
					<div role="alert">
						<Typography
							type="regular-12"
							text={error}
							className="text-main-red max-w-64 mx-auto"
						/>
					</div>
				)}
			</div>
		</div>
	);
};
